import React, { useState } from 'react';

import {
  Flexbox,
  FontAwesomeIcon,
  IconButton,
  Text,
} from '@schoolinks/ui-components';

import cn from 'classnames';
import { noop, isFunction } from 'lodash';
import { element, shape, string, bool, oneOf, func } from 'prop-types';

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import MuiChip from '@mui/material/Chip';

import Menu from '@mui/material/Menu';

import TextLink from 'components/Common/TextLink';
import WithPopover from 'components/Common/WithPopover';

import s from './Chip.css';

/**
 * Renders a compact element that represent an input, attribute, or action. Chips allow users to enter information,
 * make selections, filter content, or trigger actions.
 *
 * @param {string|element} label - Sets the text label of the Chip. Can also be an element
 * @param {element} leftComponent - Used to set the icon for MuiChip (left side)
 * @param {element} rightComponent - Used to set the deleteIcon for MuiChip (right side). onDelete function must be provided to show this.
 * @param {string} variant - Sets the variant of the Chip
 * @param {string} color - Sets the color of the Chip
 * @param {string} size - Sets the size of the Chip
 * @param {bool} isClickable - Determines if cursor is a pointer when hovering over Chip
 * @param {string} linkedText - Chip's TextLink label. This value should be provided if variant is either 'link' or 'popover'
 * @param {string} linkedHref - Chip's TextLink href. This value should be provided if variant is 'link'
 * @param {element} PopoverContent - This must be a provided component if variant is 'popover'. Refer to Storybook for an example.
 * @param {func} renderMenuItems - Renders the menu items for the edit variant.
 * @param {func} onDelete - Callback fired on click of X icon when variant is 'remove'.
 */
const Chip = React.forwardRef(
  (
    {
      classes,
      leftComponent,
      rightComponent,
      color,
      isClickable,
      size,
      variant,
      active,
      label,
      linkedText,
      linkedHref,
      linkProps,
      PopoverContent,
      renderMenuItems,
      onDelete,
      ...restProps
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const menu = 'menu';
    const menuButton = 'menuButton';
    const {
      rootClass,
      labelClass,
      muiChipClasses,
      leftComponentClass,
      rightComponentClass,
    } = classes;

    let avatarClassName = 'avatar';
    let deleteIconClassName = 'deleteIcon';

    if (size === 'small') {
      avatarClassName = 'avatarSmall';
      deleteIconClassName = 'deleteIconSmall';
    }

    const getLabel = () => {
      if (variant === 'link') {
        return (
          <Flexbox alignItems="center" gap={0.5}>
            {label}
            <TextLink
              href={linkedHref}
              variant="metaBold"
              forceUnderline
              {...linkProps}
            >
              {linkedText}
            </TextLink>
          </Flexbox>
        );
      }
      if (variant === 'popover' && PopoverContent) {
        return (
          <Flexbox alignItems="center" gap={0.5}>
            {label}
            <WithPopover
              popover={PopoverContent}
              placement="top"
              clickable
              render={() => (
                <Text
                  className={s.underline}
                  color="blueDark"
                  variant="captionReg700"
                >
                  {linkedText}
                </Text>
              )}
            />
          </Flexbox>
        );
      }
      if (variant === 'edit') {
        return (
          <Flexbox alignItems="center" gap={0.5}>
            {label}
            <IconButton
              aria-controls={open ? menu : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              id={menuButton}
              removePadding
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </IconButton>
          </Flexbox>
        );
      }
      if (variant === 'remove' || isFunction(onDelete)) {
        return (
          <Flexbox alignItems="center" gap={0.5}>
            {label}
            <IconButton aria-label="Delete" removePadding onClick={onDelete}>
              <FontAwesomeIcon icon={faXmark} color="" />
            </IconButton>
          </Flexbox>
        );
      }
      return label;
    };

    return (
      <>
        <MuiChip
          ref={ref}
          classes={{
            root: cn(rootClass, 'notranslate', {
              [s.chip]: variant !== 'add',
              [s.medium]: size === 'medium',
              [s.small]: size === 'small',
              [s.notClickable]: !isClickable,
              [s.orangeChip]: color === 'orange',
              [s.yellowChip]: color === 'yellow',
              [s.violetChip]: color === 'violet',
              [s.tealChip]: color === 'teal',
              [s.indigoChip]: color === 'indigo',
              [s.blueSteelChip]: color === 'blueSteel',
              [s.blueChip]: color === 'blue',
              [s.redChip]: color === 'red',
              [s.redDarkChip]: color === 'redDark',
              [s.purpleChip]: color === 'purple',
              [s.grayDarkChip]: color === 'grayDark',
              [s.blueExtraLightChip]: color === 'blueExtraLight',
              [s.pinkChip]: color === 'pink',
              [s.greenChip]: color === 'green',
              [s.greenDarkChip]: color === 'greenDark',
              [s.grayChip]: color === 'gray',
              [s.remove]: variant === 'remove' && onDelete,
              [s.add]: variant === 'add',
              [s.edit]: variant === 'edit',
              [s.outlined]: variant === 'outlined',
              [s.gridItem]: variant === 'gridItem',
              [s.gridItemActive]: active,
            }),
            label: cn(s.label, labelClass, {
              [s.addLabel]: variant === 'add',
              [s.gridItemLabel]: variant === 'gridItem',
            }),
            icon: s.icon,
            disabled: variant === 'gridItem' ? s.gridItemDisabled : s.disabled,
            [avatarClassName]: leftComponentClass,
            [deleteIconClassName]: rightComponentClass,
            ...muiChipClasses,
          }}
          icon={
            leftComponent ||
            (variant === 'add' ? (
              <FontAwesomeIcon color="blue" icon={faPlus} />
            ) : undefined)
          }
          label={getLabel()}
          // Apply onClick to Chip edit variant because faEllipsisVertical is difficult to click
          onClick={
            variant === 'edit' ? e => setAnchorEl(e.currentTarget) : undefined
          }
          {...restProps}
        />
        {variant === 'edit' && (
          <Menu
            id={menu}
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              'aria-labelledby': menuButton,
            }}
          >
            {renderMenuItems()}
          </Menu>
        )}
      </>
    );
  },
);

Chip.propTypes = {
  leftComponent: element,
  rightComponent: element,
  color: oneOf([
    'orange',
    'yellow',
    'violet',
    'teal',
    'indigo',
    'blue',
    'blueSteel',
    'green',
    'gray',
    'grayDark',
  ]),
  classes: shape({
    rootClass: string,
    labelClass: string,
    muiChipClasses: shape({}),
    leftComponentClass: string,
    rightComponentClass: string,
  }),
  isClickable: bool,
  active: bool,
  size: oneOf(['small', 'medium']),
  variant: string,
  label: string,
  linkedText: string,
  linkedHref: string,
  PopoverContent: element,
  renderMenuItems: func,
  linkProps: shape({}),
  onDelete: func,
};

Chip.defaultProps = {
  leftComponent: null,
  rightComponent: null,
  color: 'blue',
  classes: shape({
    rootClass: '',
    labelClass: '',
    muiChipClasses: {},
    leftComponentClass: '',
    rightComponentClass: '',
  }),
  isClickable: true,
  active: false,
  size: 'medium',
  variant: 'default',
  label: null,
  linkedText: null,
  linkedHref: null,
  PopoverContent: null,
  renderMenuItems: noop,
  linkProps: {},
  onDelete: undefined,
};

export default Chip;
